import { Injectable } from '@angular/core';
import { InvestorCapitalAccount } from '../../../models/capital-account/InvestorCapitalAccount';
import { FinancialStatementItem } from '../../../models/common/FinancialStatementItem';
import { CustomAllocation } from '../../../models/capital-account/CustomAllocation';
import {
  AllocationMethod,
  CapitalAccountData,
  CapitalAccountTotals, CarriedInterestAllocationMethod, CarriedInterestTotals, InvestorDataCA,
  PartnersTotals,
} from '../../../utils/capital-account/utils';
import { FinCalc } from "../../../../../services/financial-calculations/financial-calculations.service";

@Injectable({
  providedIn: 'root',
})
export class CapitalAccountCalculationsService {

  calculateTotalCommitment(investors: InvestorCapitalAccount[], totals: CapitalAccountTotals): void {
    const allCommitment = investors.reduce((total, investor) => total + investor.commitment, 0);
    const gpsCommitment = investors.filter((investor) => investor.isGP)
      .reduce((total, investor) => total + investor.commitment, 0);
    totals.commitment = { all: allCommitment, gps: gpsCommitment, lps: allCommitment - gpsCommitment };
  }

  calculateItemValue(item: FinancialStatementItem, investor: InvestorCapitalAccount, capitalAccountData: CapitalAccountData): number {
    const allocationType = capitalAccountData.itemAllocationMethods.find((method) => method.item.id === item.id)?.method;
    switch (allocationType) {
      case AllocationMethod.HOLDING:
        return item.sums.balance * this.calculateHolding(investor, capitalAccountData.totals) / 100;
      case AllocationMethod.HOLDING_WITHOUT_GP:
        return item.sums.balance * this.calculateHolding(investor, capitalAccountData.totals, true) / 100;
      case AllocationMethod.MANUAL:
        return 0;
      default:
        return 0;
    }
  }

  calculateHolding(investor: InvestorCapitalAccount, totals: CapitalAccountTotals, withoutGP = false): number {
    if (!investor.commitment) {
      return 0;
    }
    if (withoutGP) {
      return !investor.isGP ? (investor.commitment / totals.commitment.lps) * 100 : 0;
    }
    return investor.commitment / totals.commitment.all * 100;
  }

  /**
   * **Note**: each time item totals are changed, we need to recalculate:
   * 1. Accumulated results
   * 2. Carried interest
   * 3. NAV (done inside carried interest)
   * 4. Net Gain/Loss (done inside carried interest)
   */
  calculateItemTotals(capitalAccountData: CapitalAccountData): void {
    const parts = [capitalAccountData.partnersCapital, capitalAccountData.losses, capitalAccountData.profits];
    parts.forEach((part) => {
      part.financialStatementItems.forEach((item) => {
        const lpsTotal = this.getTotalForItemByGPStatus(item, false, capitalAccountData);
        const gpsTotal = this.getTotalForItemByGPStatus(item, true, capitalAccountData);

        capitalAccountData.totals.item[item.id] = new PartnersTotals(lpsTotal, gpsTotal, lpsTotal + gpsTotal);
      });
    });

    this.calculateAccumulatedResults(capitalAccountData);
    this.calculateCarriedInterest(capitalAccountData);
  }

  /**
   * **Note**: each time NAV totals are changed, we need to recalculate TVPI
   */
  calculateNAVs(capitalAccountData: CapitalAccountData): void {
    let totalNavLps = 0;
    let totalNavGps = 0;
    capitalAccountData.investorsData.forEach((investorData) => {
      this.calculateNAVForInvestor(investorData, capitalAccountData);
      if (investorData.investor.isGP) {
        totalNavGps += investorData.nav;
      } else {
        totalNavLps += investorData.nav;
      }
    });
    // total nav should take into account carried interest
    const totalNavAll = totalNavGps + totalNavLps + capitalAccountData.totals.carriedInterest.ciValue;
    capitalAccountData.totals.nav = { lps: totalNavLps, gps: totalNavGps, all: totalNavAll };
    this.calculateTVPI(capitalAccountData);
  }

  /**
   * **Note**: each time item custom allocations are changed, we need to recalculate Accumulated results.
   */
  calculateCustomAllocationTotals(capitalAccountData: CapitalAccountData): void {
    capitalAccountData.customAllocations.forEach((customAllocation) => {
      const lpsTotal = this.getTotalForCustomAllocationByGPStatus(customAllocation, false, capitalAccountData);
      const gpsTotal = this.getTotalForCustomAllocationByGPStatus(customAllocation, true, capitalAccountData);

      capitalAccountData.totals.customAllocation[customAllocation.uuid] = new PartnersTotals(lpsTotal, gpsTotal, lpsTotal + gpsTotal);
    });
    this.calculateAccumulatedResults(capitalAccountData);
  }

  calculateAccumulatedResults(capitalAccountData: CapitalAccountData): void {
    let totalLps = 0;
    let totalGps = 0;

    capitalAccountData.investorsData.forEach((investorData) => {
      let investorAccumulatedResults = 0;
      // take values from PL
      [capitalAccountData.profits, capitalAccountData.losses].forEach((part) => {
        part.financialStatementItems.forEach((item) => {
          const isManualAllocation = investorData.allocationMethod[item.id] === AllocationMethod.MANUAL;
          const itemValue = (isManualAllocation ? investorData.manualValue[item.id] : investorData.itemValue[item.id]) || 0;
          investorAccumulatedResults += itemValue;

          // Accumulate totals based on investor type (GP or LP)
          investorData.investor.isGP ? totalGps += itemValue : totalLps += itemValue;
        });
      });

      // take values from custom allocations
      capitalAccountData.customAllocations.forEach((customAllocation) => {
        const customAllocationValue = investorData.customAllocationValue[customAllocation.uuid] || 0;
        investorAccumulatedResults += customAllocationValue;
        investorData.investor.isGP ? totalGps += customAllocationValue : totalLps += customAllocationValue;
      });

      investorData.accumulatedResults = investorAccumulatedResults;
    });

    capitalAccountData.totals.accumulatedResults = new PartnersTotals(totalLps, totalGps,
      totalLps + totalGps);
  }

  /**
   * Calculates the carried interest for each investor and the totals.
   * For investors without a manual percentage (*manualPct*), the common percentage (*inputPct*)
   * from the *CapitalAccountData.carriedInterest.inputPct* is used.
   * For investors with a set manualPct, their carried interest is calculated using this value.<br>
   * **Notes**:
   * 1. General Partners (GPs) normally do not have Carried Interest.
   * Carried interest for GPs is calculated only if *manualPct* is explicitly set.
   * 2. Each time carried interest is calculated, the NAV values are recalculated.
   * 3. Each time carried interest is calculated, the Net Gain/Loss results are recalculated.
   */
  calculateCarriedInterest(capAccData: CapitalAccountData): void {
    let totalNglLps = 0;
    let totalNglGps = 0;
    capAccData.investorsData.forEach((investorData) => {
      if (capAccData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.CALCULATED) {
        let calcValue: number;

        if (investorData.investor.isGP && investorData.carriedInterest.manualPct === null) {
          // If the investor is a GP and does not have a manualPct set, set carried interest to 0
          calcValue = 0;
        } else if (investorData.carriedInterest.manualPct !== null) {
          // Otherwise, if the investor has a manualPct set (regardless of being a GP or not, might be 0)
          calcValue = (-1) * investorData.carriedInterest.manualPct * investorData.accumulatedResults;
        } else {
          // If no manualPct is set and the investor is not a GP, use the common percentage (inputPct)
          calcValue = (-1) * capAccData.carriedInterest.manualPct * investorData.accumulatedResults;
        }

        // Ensuring it's not a positive number
        investorData.carriedInterest.calculatedValue = Math.min(calcValue, 0);
        investorData.investor.isGP ?
          totalNglGps += investorData.carriedInterest.calculatedValue : totalNglLps += investorData.carriedInterest.calculatedValue;
      } else {
        investorData.investor.isGP ?
          totalNglGps += investorData.carriedInterest.manualValue : totalNglLps += investorData.carriedInterest.manualValue;
      }
    });

    capAccData.totals.carriedInterest = new CarriedInterestTotals(totalNglLps, totalNglGps, totalNglLps + totalNglGps);
    capAccData.totals.carriedInterest.ciValue = capAccData.totals.carriedInterest.all * (-1);
    capAccData.totals.carriedInterest.subtotalGps = capAccData.totals.carriedInterest.gps + capAccData.totals.carriedInterest.ciValue;
    capAccData.totals.carriedInterest.grandTotal = capAccData.totals.carriedInterest.all + capAccData.totals.carriedInterest.ciValue;

    this.calculateNAVs(capAccData);
    this.calculateNetGainLoss(capAccData);
  }


  calculateNetGainLoss(capitalAccountData: CapitalAccountData): void {
    let totalNglLps = 0;
    let totalNglGps = 0;
    capitalAccountData.investorsData.forEach((investorData) => {
      const carriedInterest = (capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.CALCULATED)
        ? investorData.carriedInterest.calculatedValue : investorData.carriedInterest.manualValue;
      investorData.ngl = investorData.accumulatedResults + carriedInterest;
      investorData.investor.isGP ? totalNglGps += investorData.ngl : totalNglLps += investorData.ngl;
    });
    capitalAccountData.totals.ngl = new PartnersTotals(totalNglLps, totalNglGps, totalNglLps + totalNglGps);
  }

  calculateTVPI(capitalAccountData: CapitalAccountData): void {
    const tvpiItemsIds = capitalAccountData.tvpiItemsIds;

    capitalAccountData.investorsData.forEach((investorData) => {
      if (!investorData.nav) {
        investorData.tvpi = 0;
        return;
      }
      const distribution = this.getValueByItemId(investorData, tvpiItemsIds.distribution);
      const contributionReceivedInAdvance = this.getValueByItemId(investorData, tvpiItemsIds.contributionReceivedInAdvance);
      const contribution = this.getValueByItemId(investorData, tvpiItemsIds.contribution);
      const dueToCapitalContribution = this.getValueByItemId(investorData, tvpiItemsIds.dueToCapitalContribution);

      investorData.tvpi = FinCalc.tvpi(investorData.nav, distribution, contributionReceivedInAdvance,
        contribution, dueToCapitalContribution);
    });

    const totals = new PartnersTotals();
    const groups = Object.keys(totals);
    for (const group of groups) {
      const dist = (capitalAccountData.totals.item[tvpiItemsIds.distribution]?.[group] as number) ?? 0;
      const contrRecInAdv = (capitalAccountData.totals.item[tvpiItemsIds.contributionReceivedInAdvance]?.[group] as number) ?? 0;
      const contr = capitalAccountData.totals.item[tvpiItemsIds.contribution]?.[group] as number ?? 0;
      const dueToCapitalContribution = capitalAccountData.totals.item[tvpiItemsIds.dueToCapitalContribution]?.[group] as number ?? 0;
      const nav = capitalAccountData.totals.nav?.[group] as number ?? 0;
      totals[group] = FinCalc.tvpi(nav, dist, contrRecInAdv, contr, dueToCapitalContribution);
    }
    capitalAccountData.totals.tvpi = totals;
  }

  private getValueByItemId(investorData: InvestorDataCA, itemId: number): number {
    if (!itemId) {
      return 0;
    }
    const allocationMethod = investorData.allocationMethod[itemId];
    return allocationMethod === AllocationMethod.MANUAL
      ? (investorData.manualValue[itemId] ?? 0)
      : (investorData.itemValue[itemId] ?? 0);
  }

  private getTotalForItemByGPStatus(item: FinancialStatementItem, gp: boolean, capitalAccountData: CapitalAccountData): number {
    return capitalAccountData.investorsData
      .filter((investorData) => investorData.investor.isGP === gp)
      .reduce((total, investorData) => {
        if (investorData.allocationMethod[item.id] === AllocationMethod.MANUAL) {
          return total + (investorData.manualValue[item.id] || 0);
        }
        return total + investorData.itemValue[item.id];
      }, 0);
  }

  private calculateNAVForInvestor(investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): void {
    let total = 0;
    for (const itemId in investorData.allocationMethod) {
      if (investorData.allocationMethod[itemId] === AllocationMethod.MANUAL) {
        total += investorData.manualValue[itemId] || 0;
      } else {
        total += investorData.itemValue[itemId] || 0;
      }
    }

    for (const customAllocationValueUuid in investorData.customAllocationValue) {
      total += investorData.customAllocationValue[customAllocationValueUuid] || 0;
    }

    total += this.getCarriedInterestValueForInvestor(investorData, capitalAccountData);

    investorData.nav = total;
  }

  private getTotalForCustomAllocationByGPStatus(customAllocation: CustomAllocation, gp: boolean, capAccData: CapitalAccountData): number {
    return capAccData.investorsData
      .filter((investorData) => investorData.investor.isGP === gp)
      .reduce((total, investorData) =>
        total + (investorData.customAllocationValue[customAllocation.uuid] || 0), 0);
  }

  private getCarriedInterestValueForInvestor(investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): number {
    if (capitalAccountData.carriedInterest.selector.selectedMethod === CarriedInterestAllocationMethod.CALCULATED) {
      return investorData.carriedInterest.calculatedValue;
    }
    return investorData.carriedInterest.manualValue;
  }
}
